import {AnyImageOpResult, AnyImageOpResultNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {isImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-utils"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpSystem, REF_COUNT_DEBUG_ID_IMAGE_OP_SYSTEM_EVALUATE_RESULT} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-system"

// Reads a single (1x1) evaluated HAL image's pixel back into a plain JS Float32Array — one value
// per channel. Releases the ref added by ImageOpSystem.evaluate().
export async function readTexel(image: AnyImageOpResult): Promise<Float32Array> {
    if (!isImageOpHal(image)) {
        throw new Error("Expected a HAL image result")
    }
    const ref = await image.getUpdatedHalImage({x: 0, y: 0, width: 1, height: 1, mipLevel: 0})
    try {
        return ref.halImage.readImageDataFloat()
    } finally {
        ref.release()
        image.release(REF_COUNT_DEBUG_ID_IMAGE_OP_SYSTEM_EVALUATE_RESULT)
    }
}

// Builds a single node in a throwaway ImageOpNodeBag (never touches the operator-stack pipeline),
// evaluates it, and reads back its 1x1 result. The common shape for "run one GPU reduction over an
// image and get plain JS numbers back" — e.g. computing a per-texture-type cached statistic outside
// the live graph (see operator-stack/operators/abstract-base/cached-upstream-value.ts).
export async function evaluateAndReadTexel(
    imageOpSystem: ImageOpSystem,
    debugId: string,
    buildNode: (bag: ImageOpNodeBag) => AnyImageOpResultNode,
): Promise<Float32Array> {
    const bag = new ImageOpNodeBag(debugId)
    bag.beginFrame()
    const node = buildNode(bag)
    bag.endFrame()
    try {
        const evaluated = await imageOpSystem.evaluate("hal", node)
        return await readTexel(evaluated)
    } finally {
        bag.dispose()
    }
}
